import { doubleMetaphone } from 'double-metaphone';

export function compareTexts(original: string, spoken: string) {
  const cleanText = (text: string) =>
    text
      .toLowerCase()
      .replace(/'s\b/g, 's')
      // Remove all common punctuation
      .replace(/[.,#!$%^&*;:{}=_`~()?"'[\]<>]/g, '')
      .replace(/-/g, ' ')
      .replace(/\//g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const originalWords = cleanText(original).split(' ');
  const spokenWords = cleanText(spoken).split(' ');

  const results = originalWords.map((word, index) => {
    const spokenWord = spokenWords[index] || '';
    const isMatch = word === spokenWord;
    const similarity = calculateSimilarity(word, spokenWord);
    
    return {
      original: word,
      spoken: spokenWord,
      isMatch,
      similarity,
      status: isMatch ? 'correct' : similarity > 0.7 ? 'close' : 'incorrect',
    };
  });

  return results;
}

function calculateSimilarity(str1: string, str2: string): number {
  if (str1 === str2) return 1;
  if (!str1 || !str2) return 0;

  const len1 = str1.length;
  const len2 = str2.length;
  const matrix: number[][] = Array(len1 + 1)
    .fill(null)
    .map(() => Array(len2 + 1).fill(0));

  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  const maxLen = Math.max(len1, len2);
  return (maxLen - matrix[len1][len2]) / maxLen;
}

export function compareTextsV2(original: string, spoken: string) {
  const cleanText = (text: string) =>
    text
      .toLowerCase()
      .replace(/\bwon't\b/g, 'will not')
      .replace(/\bcan't\b/g, 'cannot')
      .replace(/n't\b/g, ' not')
      .replace(/'s\b/g, 's')
      .replace(/'re\b/g, ' are')
      .replace(/'ve\b/g, ' have')
      .replace(/'d\b/g, ' would')
      .replace(/'ll\b/g, ' will')
      .replace(/'m\b/g, ' am')
      // Remove all common punctuation including ellipsis
      .replace(/[.,#!$%^&*;:{}=_`~()?"'[\]<>…]/g, '')
      .replace(/\.\.\./g, '') // Remove ellipsis (...)
      // Replace hyphens and slashes with spaces
      .replace(/-/g, ' ')
      .replace(/\//g, ' ')
      .replace(/;/g, ' ')
      // Remove extra spaces after all replacements
      .replace(/\s+/g, ' ')
      .trim();

  // Split and filter out empty strings
  const originalWords = cleanText(original).split(' ').filter(Boolean);
  const spokenWords = cleanText(spoken).split(' ').filter(Boolean);

  const n = originalWords.length;
  const m = spokenWords.length;

  const dp: number[][] = Array(n + 1)
    .fill(null)
    .map(() => Array(m + 1).fill(0));
  const backtrack: ('diagonal' | 'up' | 'left')[][] = Array(n + 1)
    .fill(null)
    .map(() => Array(m + 1).fill(null));

  // Initialize dp matrix
  dp[0][0] = 0;
  for (let i = 1; i <= n; i++) {
    dp[i][0] = dp[i - 1][0] + 1; // Deletion cost
    backtrack[i][0] = 'up';
  }

  for (let j = 1; j <= m; j++) {
    dp[0][j] = dp[0][j - 1] + 1; // Insertion cost
    backtrack[0][j] = 'left';
  }

  // Fill dp matrix
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const costSubstitution =
        dp[i - 1][j - 1] +
        substitutionCost(originalWords[i - 1], spokenWords[j - 1]);
      const costInsertion = dp[i][j - 1] + 1; // Insertion cost
      const costDeletion = dp[i - 1][j] + 1; // Deletion cost

      const minCost = Math.min(costSubstitution, costInsertion, costDeletion);
      dp[i][j] = minCost;

      if (minCost === costSubstitution) {
        backtrack[i][j] = 'diagonal';
      } else if (minCost === costInsertion) {
        backtrack[i][j] = 'left';
      } else {
        backtrack[i][j] = 'up';
      }
    }
  }

  // Backtrack to get aligned sequences
  let i = n;
  let j = m;

  const alignedOriginalWords: (string | null)[] = [];
  const alignedSpokenWords: (string | null)[] = [];
  const alignedIsMatch: boolean[] = [];
  const alignedSimilarity: number[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0) {
      if (backtrack[i][j] === 'diagonal') {
        const word1 = originalWords[i - 1];
        const word2 = spokenWords[j - 1];
        const similarity = calculateSimilarityV2(word1, word2);
        alignedOriginalWords.unshift(word1);
        alignedSpokenWords.unshift(word2);
        alignedIsMatch.unshift(word1 === word2);
        alignedSimilarity.unshift(similarity);
        i--;
        j--;
      } else if (backtrack[i][j] === 'up') {
        const word1 = originalWords[i - 1];
        alignedOriginalWords.unshift(word1);
        alignedSpokenWords.unshift('');
        alignedIsMatch.unshift(false);
        alignedSimilarity.unshift(0);
        i--;
      } else if (backtrack[i][j] === 'left') {
        const word2 = spokenWords[j - 1];
        alignedOriginalWords.unshift('');
        alignedSpokenWords.unshift(word2);
        alignedIsMatch.unshift(false);
        alignedSimilarity.unshift(0);
        j--;
      }
    } else if (i > 0) {
      const word1 = originalWords[i - 1];
      alignedOriginalWords.unshift(word1);
      alignedSpokenWords.unshift('');
      alignedIsMatch.unshift(false);
      alignedSimilarity.unshift(0);
      i--;
    } else if (j > 0) {
      const word2 = spokenWords[j - 1];
      alignedOriginalWords.unshift('');
      alignedSpokenWords.unshift(word2);
      alignedIsMatch.unshift(false);
      alignedSimilarity.unshift(0);
      j--;
    }
  }

  const results = alignedOriginalWords.map((originalWord, idx) => {
    const spokenWord = alignedSpokenWords[idx] || '';
    let isMatch = alignedIsMatch[idx];
    
    // Check phonetics if not an exact string match
    if (!isMatch && originalWord && spokenWord) {
      const [p1, s1] = doubleMetaphone(originalWord);
      const [p2, s2] = doubleMetaphone(spokenWord);
      if (
        (p1 && p2 && p1 === p2) || 
        (s1 && s2 && s1 === s2) || 
        (p1 && s2 && p1 === s2) || 
        (s1 && p2 && s1 === p2)
      ) {
        isMatch = true;
      }
    }

    const similarity = alignedSimilarity[idx];
    const status = isMatch
      ? 'correct'
      : similarity > 0.7
      ? 'close'
      : 'incorrect';

    return {
      original: originalWord || '',
      spoken: spokenWord || '',
      isMatch,
      similarity,
      status,
    };
  });

  return results;
}

function substitutionCost(word1: string, word2: string): number {
  if (word1 === word2) return 0;
  
  // Use double-metaphone to check if words sound the same
  const [p1, s1] = doubleMetaphone(word1);
  const [p2, s2] = doubleMetaphone(word2);
  
  // If either the primary or secondary phonetic codes match, consider it a 100% match
  if (
    (p1 && p2 && p1 === p2) || 
    (s1 && s2 && s1 === s2) || 
    (p1 && s2 && p1 === s2) || 
    (s1 && p2 && s1 === p2)
  ) {
    return 0;
  }

  const similarity = calculateSimilarityV2(word1, word2);
  return 1 - similarity;
}

function calculateSimilarityV2(str1: string, str2: string): number {
  if (str1 === str2) return 1;
  if (!str1 || !str2) return 0;

  const len1 = str1.length;
  const len2 = str2.length;
  const matrix: number[][] = Array(len1 + 1)
    .fill(null)
    .map(() => Array(len2 + 1).fill(0));

  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1, // deletion
        matrix[i][j - 1] + 1, // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  const maxLen = Math.max(len1, len2);
  return (maxLen - matrix[len1][len2]) / maxLen;
}

export interface CharDiffItem {
  char: string;
  expectedChar?: string;
  type: 'correct' | 'incorrect' | 'missing' | 'extra';
}

export interface DetailedWordDiff {
  expected: string;
  spoken: string;
  similarity: number;
  isExact: boolean;
  isPhoneticMatch: boolean;
  expectedChars: CharDiffItem[];
  spokenChars: CharDiffItem[];
  explanation: string;
  howToFix: string;
}

/**
 * Computes character-level diff and pedagogical explanation for pronunciation mismatches.
 */
export function getDetailedWordDiff(expectedWord: string, spokenWord: string): DetailedWordDiff {
  const cleanExp = (expectedWord || '').toLowerCase().replace(/[^a-z0-9']/gi, '').trim();
  const cleanSpk = (spokenWord || '').toLowerCase().replace(/[^a-z0-9']/gi, '').trim();

  const isExact = cleanExp === cleanSpk;
  const similarity = calculateSimilarityV2(cleanExp, cleanSpk);

  let isPhoneticMatch = false;
  if (cleanExp && cleanSpk) {
    const [p1, s1] = doubleMetaphone(cleanExp);
    const [p2, s2] = doubleMetaphone(cleanSpk);
    if (
      (p1 && p2 && p1 === p2) ||
      (s1 && s2 && s1 === s2) ||
      (p1 && s2 && p1 === s2) ||
      (s1 && p2 && s1 === p2)
    ) {
      isPhoneticMatch = true;
    }
  }

  if (isExact) {
    const chars: CharDiffItem[] = cleanExp.split('').map(char => ({ char, type: 'correct' }));
    return {
      expected: cleanExp,
      spoken: cleanSpk,
      similarity: 1,
      isExact: true,
      isPhoneticMatch: true,
      expectedChars: chars,
      spokenChars: chars,
      explanation: 'Pronúncia perfeita!',
      howToFix: 'Continue assim!'
    };
  }

  // Needleman-Wunsch / Levenshtein alignment on characters
  const n = cleanExp.length;
  const m = cleanSpk.length;
  const dp: number[][] = Array(n + 1).fill(null).map(() => Array(m + 1).fill(0));
  const backtrack: ('diag' | 'up' | 'left')[][] = Array(n + 1).fill(null).map(() => Array(m + 1).fill(null));

  for (let i = 1; i <= n; i++) {
    dp[i][0] = i;
    backtrack[i][0] = 'up';
  }
  for (let j = 1; j <= m; j++) {
    dp[0][j] = j;
    backtrack[0][j] = 'left';
  }

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const match = cleanExp[i - 1] === cleanSpk[j - 1];
      const costDiag = dp[i - 1][j - 1] + (match ? 0 : 1);
      const costUp = dp[i - 1][j] + 1; // deletion from expected
      const costLeft = dp[i][j - 1] + 1; // insertion in spoken

      const minCost = Math.min(costDiag, costUp, costLeft);
      dp[i][j] = minCost;

      if (minCost === costDiag) {
        backtrack[i][j] = 'diag';
      } else if (minCost === costLeft) {
        backtrack[i][j] = 'left';
      } else {
        backtrack[i][j] = 'up';
      }
    }
  }

  // Backtrack
  let i = n;
  let j = m;
  const alignedExpected: (string | null)[] = [];
  const alignedSpoken: (string | null)[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && backtrack[i][j] === 'diag') {
      alignedExpected.unshift(cleanExp[i - 1]);
      alignedSpoken.unshift(cleanSpk[j - 1]);
      i--;
      j--;
    } else if (j > 0 && (i === 0 || backtrack[i][j] === 'left')) {
      alignedExpected.unshift(null);
      alignedSpoken.unshift(cleanSpk[j - 1]);
      j--;
    } else {
      alignedExpected.unshift(cleanExp[i - 1]);
      alignedSpoken.unshift(null);
      i--;
    }
  }

  const expectedChars: CharDiffItem[] = [];
  const spokenChars: CharDiffItem[] = [];

  for (let k = 0; k < alignedExpected.length; k++) {
    const e = alignedExpected[k];
    const s = alignedSpoken[k];

    if (e !== null && s !== null) {
      if (e === s) {
        expectedChars.push({ char: e, type: 'correct' });
        spokenChars.push({ char: s, type: 'correct' });
      } else {
        expectedChars.push({ char: e, expectedChar: s, type: 'incorrect' });
        spokenChars.push({ char: s, expectedChar: e, type: 'incorrect' });
      }
    } else if (e !== null && s === null) {
      expectedChars.push({ char: e, type: 'missing' });
    } else if (e === null && s !== null) {
      spokenChars.push({ char: s, type: 'extra' });
    }
  }

  // Generate actionable Brazilian pedagogical explanation
  let explanation = '';
  let howToFix = '';

  const missedChars = expectedChars.filter(c => c.type === 'missing').map(c => c.char).join('');
  const extraChars = spokenChars.filter(c => c.type === 'extra').map(c => c.char).join('');

  if (cleanExp.startsWith('th') && (cleanSpk.startsWith('f') || cleanSpk.startsWith('s') || cleanSpk.startsWith('t'))) {
    explanation = `Você pronunciou com som de "${cleanSpk[0].toUpperCase()}", mas o som correto de "th" exige a ponta da língua entre os dentes.`;
    howToFix = 'Coloque a ponta da língua entre os dentes da frente e solte o ar sem usar as cordas vocais.';
  } else if (cleanExp.includes('l') && !cleanSpk.includes('l') && cleanExp.replace('l', '') === cleanSpk) {
    explanation = `Você omitiu o som da letra "L" na palavra (soou como "${cleanSpk}").`;
    howToFix = 'Enrole a língua e toque a ponta no céu da boca para articular o som de L.';
  } else if (extraChars.includes('i') || extraChars.includes('y') || cleanSpk.endsWith('i') || cleanSpk.endsWith('e')) {
    explanation = `Você acrescentou um som de vogal ("i") no final da palavra.`;
    howToFix = `Trave a pronúncia diretamente na consoante final "${cleanExp.slice(-1).toUpperCase()}". Não fale com som de "i" no fim.`;
  } else if (missedChars.length > 0) {
    explanation = `Faltou articular o som das letras "${missedChars.toUpperCase()}" na palavra.`;
    howToFix = `Preste atenção em pronunciar claramente "${cleanExp}".`;
  } else {
    explanation = `Ouvimos "${cleanSpk}", mas o esperado era "${cleanExp}".`;
    howToFix = `Ouça o áudio do sistema com atenção e tente imitar o ritmo e os sons.`;
  }

  return {
    expected: cleanExp,
    spoken: cleanSpk,
    similarity,
    isExact: false,
    isPhoneticMatch,
    expectedChars,
    spokenChars,
    explanation,
    howToFix
  };
}
